import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiMapPin, FiNavigation, FiRefreshCcw, FiSend, FiSmartphone, FiTruck } from 'react-icons/fi';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { buscarPainelMotoboy, enviarLocalizacaoMotoboy } from '../../api/rastreamento';
import { PainelMotoboyData } from '../../types';
import { formatarMoeda } from '../../utils/formatadores';

const ENVIO_MS = 15000;

type PosicaoAtual = {
  latitude: number;
  longitude: number;
  precisao_metros?: number | null;
};

function formatarDataHora(valor?: string | null) {
  if (!valor) return '-';
  return new Date(valor).toLocaleString('pt-BR');
}

function enderecoEntrega(pedido: PainelMotoboyData['pedidos_ativos'][number] | null) {
  if (!pedido) return 'Selecione um pedido para ver o destino.';
  const linha = [pedido.logradouro, pedido.numero_endereco].filter(Boolean).join(', ');
  const bairroCidade = [pedido.bairro, pedido.cidade, pedido.uf].filter(Boolean).join(' - ');
  const complemento = pedido.complemento ? `, ${pedido.complemento}` : '';
  return [linha ? `${linha}${complemento}` : null, bairroCidade].filter(Boolean).join(' | ') || 'Endereco nao informado.';
}

export function PainelMotoboy() {
  const params = useParams();
  const tenantId = Number(params.tenantId || 0);
  const token = params.token || '';
  const [dados, setDados] = useState<PainelMotoboyData | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pedidoId, setPedidoId] = useState<string>('');
  const [ativo, setAtivo] = useState(false);
  const [ultimaPosicao, setUltimaPosicao] = useState<PosicaoAtual | null>(null);
  const [ultimaSincronia, setUltimaSincronia] = useState<string | null>(null);
  const [statusRastreamento, setStatusRastreamento] = useState('Aguardando permissao');
  const watchIdRef = useRef<number | null>(null);
  const intervaloRef = useRef<number | null>(null);
  const ultimaPosicaoRef = useRef<PosicaoAtual | null>(null);

  const carregarPainel = useCallback(async (exibirLoading = false) => {
    if (exibirLoading) setAtualizando(true);

    try {
      const resposta = await buscarPainelMotoboy(tenantId, token);
      setDados(resposta);
      setErro(null);
    } catch (error: any) {
      setErro(error.response?.data?.erro || 'Nao foi possivel carregar o painel do motoboy.');
    } finally {
      setCarregando(false);
      setAtualizando(false);
    }
  }, [tenantId, token]);

  useEffect(() => {
    void carregarPainel();

    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (intervaloRef.current !== null) window.clearInterval(intervaloRef.current);
    };
  }, [carregarPainel]);

  useEffect(() => {
    if (!dados?.pedidos_ativos.length) {
      setPedidoId('');
      return;
    }

    const pedidoAindaExiste = dados.pedidos_ativos.some((pedido) => String(pedido.id) === pedidoId);
    if (!pedidoAindaExiste) {
      setPedidoId(String(dados.pedidos_ativos[0].id));
    }
  }, [dados, pedidoId]);

  async function sincronizarPosicao(posicao?: PosicaoAtual | null) {
    const atual = posicao || ultimaPosicaoRef.current;
    if (!atual) return;

    try {
      await enviarLocalizacaoMotoboy(tenantId, token, {
        latitude: atual.latitude,
        longitude: atual.longitude,
        precisao_metros: atual.precisao_metros ?? null,
        pedido_id: pedidoId ? Number(pedidoId) : null,
      });
      setUltimaSincronia(new Date().toISOString());
      setStatusRastreamento('Localizacao enviada com sucesso');
    } catch (error: any) {
      setStatusRastreamento(error.response?.data?.erro || 'Falha ao enviar a localizacao');
    }
  }

  function encerrarRastreamento() {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervaloRef.current !== null) {
      window.clearInterval(intervaloRef.current);
      intervaloRef.current = null;
    }
    setAtivo(false);
    setStatusRastreamento('Rastreamento pausado');
  }

  function iniciarRastreamento() {
    if (!navigator.geolocation) {
      setStatusRastreamento('Seu navegador nao suporta geolocalizacao.');
      return;
    }

    setStatusRastreamento('Solicitando permissao de localizacao...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const inicial = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          precisao_metros: position.coords.accuracy ?? null,
        };
        setUltimaPosicao(inicial);
        ultimaPosicaoRef.current = inicial;
        setAtivo(true);
        await sincronizarPosicao(inicial);

        watchIdRef.current = navigator.geolocation.watchPosition(
          (watchPosition) => {
            const atual = {
              latitude: watchPosition.coords.latitude,
              longitude: watchPosition.coords.longitude,
              precisao_metros: watchPosition.coords.accuracy ?? null,
            };
            setUltimaPosicao(atual);
            ultimaPosicaoRef.current = atual;
            setStatusRastreamento('Posicao atualizada no celular');
          },
          (error) => {
            setStatusRastreamento(error.message || 'Nao foi possivel acompanhar a posicao.');
            encerrarRastreamento();
          },
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
        );

        intervaloRef.current = window.setInterval(() => {
          void sincronizarPosicao();
        }, ENVIO_MS);
      },
      (error) => {
        setStatusRastreamento(error.message || 'Permissao de localizacao negada.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  const pedidoSelecionado = useMemo(
    () => dados?.pedidos_ativos.find((pedido) => String(pedido.id) === pedidoId) || null,
    [dados, pedidoId]
  );

  return (
    <div className="lizzi-motoboy-page">
      <div className="lizzi-motoboy-shell">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div>
            <p className="lizzi-motoboy-kicker">Entrega em tempo real</p>
            <h1 style={{ margin: '4px 0 6px', fontSize: '30px' }}>Painel do motoboy</h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.78)' }}>
              Mantenha esta tela aberta durante a rota para atualizar sua localizacao.
            </p>
          </div>
          <Button type="button" variant="outline" fullWidth={false} onClick={() => void carregarPainel(true)} disabled={atualizando}>
            <FiRefreshCcw size={15} style={{ marginRight: 6 }} />
            {atualizando ? 'Atualizando...' : 'Atualizar pedidos'}
          </Button>
        </div>

        {carregando ? (
          <Card>
            <p style={{ margin: 0 }}>Carregando painel...</p>
          </Card>
        ) : erro || !dados ? (
          <Card>
            <p style={{ marginBottom: '12px' }}>{erro || 'Link nao encontrado.'}</p>
            <Button type="button" fullWidth={false} onClick={() => void carregarPainel(true)}>
              Tentar novamente
            </Button>
          </Card>
        ) : (
          <>
            <div className="lizzi-motoboy-grid">
              <Card className="lizzi-motoboy-card">
                <div className="lizzi-motoboy-card-head">
                  <span className="lizzi-motoboy-icon">
                    <FiSmartphone size={18} />
                  </span>
                  <div>
                    <strong>{dados.motoboy.nome}</strong>
                    <p>{dados.motoboy.celular || 'Celular nao informado'}</p>
                  </div>
                </div>

                <label className="lizzi-motoboy-label" htmlFor="pedido-atual">
                  Pedido atual
                </label>
                <select
                  id="pedido-atual"
                  className="lizzi-motoboy-select"
                  value={pedidoId}
                  onChange={(event) => setPedidoId(event.target.value)}
                >
                  <option value="">Sem pedido vinculado no momento</option>
                  {dados.pedidos_ativos.map((pedido) => (
                    <option key={pedido.id} value={pedido.id}>
                      {pedido.numero_pedido ? `#${pedido.numero_pedido}` : `#${pedido.id}`} - {pedido.identificacao_cliente || 'Entrega'} - {pedido.status}
                    </option>
                  ))}
                </select>

                {pedidoSelecionado ? (
                  <div className="lizzi-motoboy-order-box">
                    <strong>{pedidoSelecionado.numero_pedido ? `Pedido #${pedidoSelecionado.numero_pedido}` : `Pedido #${pedidoSelecionado.id}`}</strong>
                    <span>{pedidoSelecionado.identificacao_cliente || 'Entrega em rota'}</span>
                    <small>
                      {pedidoSelecionado.status} | {formatarMoeda(pedidoSelecionado.total)}
                    </small>
                  </div>
                ) : null}

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {!ativo ? (
                    <Button type="button" onClick={iniciarRastreamento} fullWidth={false}>
                      <FiNavigation size={15} style={{ marginRight: 6 }} />
                      Iniciar rastreamento
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={encerrarRastreamento} fullWidth={false}>
                      Pausar rastreamento
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => void sincronizarPosicao()} fullWidth={false} disabled={!ultimaPosicao}>
                    <FiSend size={15} style={{ marginRight: 6 }} />
                    Enviar agora
                  </Button>
                </div>
              </Card>

              <Card className="lizzi-motoboy-card">
                <div className="lizzi-motoboy-card-head">
                  <span className="lizzi-motoboy-icon">
                    <FiTruck size={18} />
                  </span>
                  <div>
                    <strong>Destino da entrega</strong>
                    <p>{pedidoSelecionado ? 'Endereco selecionado para a rota' : 'Escolha um pedido acima'}</p>
                  </div>
                </div>

                <div className="lizzi-motoboy-destino">{enderecoEntrega(pedidoSelecionado)}</div>

                <div className="lizzi-motoboy-pedidos-lista">
                  {dados.pedidos_ativos.map((pedido) => (
                    <button
                      key={pedido.id}
                      type="button"
                      className={`lizzi-motoboy-pedido-chip ${String(pedido.id) === pedidoId ? 'ativo' : ''}`}
                      onClick={() => setPedidoId(String(pedido.id))}
                    >
                      {pedido.numero_pedido ? `#${pedido.numero_pedido}` : `#${pedido.id}`}
                    </button>
                  ))}
                </div>
              </Card>
            </div>

            <div style={{ marginTop: '16px' }}>
              <Card className="lizzi-motoboy-card">
                <div className="lizzi-motoboy-card-head">
                  <span className="lizzi-motoboy-icon">
                    <FiMapPin size={18} />
                  </span>
                  <div>
                    <strong>Status do rastreamento</strong>
                    <p>{ativo ? 'Ativo no celular' : 'Parado'}</p>
                  </div>
                </div>

                <div className="lizzi-motoboy-status">{statusRastreamento}</div>

                <div className="lizzi-motoboy-metrics">
                  <div>
                    <span>Ultima leitura</span>
                    <strong>
                      {ultimaPosicao ? `${ultimaPosicao.latitude.toFixed(5)}, ${ultimaPosicao.longitude.toFixed(5)}` : '-'}
                    </strong>
                  </div>
                  <div>
                    <span>Precisao</span>
                    <strong>{ultimaPosicao?.precisao_metros ? `${Math.round(ultimaPosicao.precisao_metros)} m` : '-'}</strong>
                  </div>
                  <div>
                    <span>Ultimo envio</span>
                    <strong>{formatarDataHora(ultimaSincronia)}</strong>
                  </div>
                </div>

                <p style={{ margin: 0, color: '#5c6b84', fontSize: '13px' }}>
                  O sistema envia sua posicao a cada 15 segundos enquanto esta tela estiver ativa.
                </p>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
